'use client'

import { createContext, useContext, useMemo } from 'react'
import {
  findActivePath,
  isBranchActive,
  isItemActive,
} from '../utils/activeNav'

const DEFAULT_ACTIVE_NAV = {
  activePath: null,
  host: null,
  isActive: () => false,
  isActiveBranch: () => false,
}

const ActiveNavContext = createContext(DEFAULT_ACTIVE_NAV)

/**
 * Resolves which navigation entry matches the current URL. Computed once and
 * shared so every subnav variant highlights the same entry.
 * @param {Array} navigation - Navigation tree
 * @param {string|null} currentUrl - Current browser URL
 */
export const useActiveNavValue = (navigation, currentUrl) => {
  return useMemo(() => {
    let host = null

    if (currentUrl && /^(https?:)?\/\//i.test(currentUrl)) {
      try {
        host = new URL(
          currentUrl.startsWith('//') ? `https:${currentUrl}` : currentUrl
        ).host
      } catch {
        host = null
      }
    }

    const activePath = findActivePath(navigation, currentUrl)

    return {
      activePath,
      host,
      isActive: (item) => isItemActive(item, activePath, host),
      isActiveBranch: (item) => isBranchActive(item, activePath, host),
    }
  }, [navigation, currentUrl])
}

export const ActiveNavProvider = ({ value, children }) => (
  <ActiveNavContext.Provider value={value ?? DEFAULT_ACTIVE_NAV}>
    {children}
  </ActiveNavContext.Provider>
)

export const useActiveNav = () => useContext(ActiveNavContext)

export default ActiveNavContext
